import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, LayoutDashboard, Search, User as UserIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LOGO_DARK_BG, LOGO_LIGHT_BG } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';
import { useSiteConfig } from '../../lib/siteConfig';

const slugify = (s) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const transparent = isHome && !scrolled;
  const cfg = useSiteConfig();
  const menu = (cfg.menu || [])
    .filter((m) => m.visible)
    .filter((m) => {
      // Hide /mapa and /propiedades when there is no logged-in user
      if (!user && (m.to === '/mapa' || m.to === '/propiedades')) return false;
      return true;
    });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const panelHref = user ? '/app' : '/login';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        transparent ? 'bg-transparent' : 'bg-white shadow-lg shadow-cnib-navy/10'
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 md:h-24 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center" data-testid="nav-brand-logo">
          <img
            src={transparent ? LOGO_DARK_BG : LOGO_LIGHT_BG}
            alt="CNIB — Cámara Nacional Inmobiliaria de Bolivia"
            className="h-14 w-auto object-contain md:h-16 lg:h-20"
          />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-6 lg:flex">
          {menu.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${slugify(link.label)}`}
              className={({ isActive }) =>
                `group relative py-1 text-sm font-semibold transition-colors ${
                  transparent
                    ? isActive
                      ? 'text-white'
                      : 'text-white/75 hover:text-white'
                    : isActive
                      ? 'text-cnib-navy'
                      : 'text-slate-500 hover:text-cnib-navy'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-0.5 rounded-full bg-orange-500 transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          <Link
            to="/noticias"
            aria-label="Buscar"
            data-testid="nav-search-button"
            className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
              transparent ? 'text-white hover:bg-white/10' : 'text-cnib-navy hover:bg-slate-100'
            }`}
          >
            <Search className="h-5 w-5" />
          </Link>
          {user ? (
            <Link
              to={panelHref}
              data-testid="nav-dashboard-button"
              className={`group flex items-center gap-2.5 rounded-full py-1.5 pl-1.5 pr-4 text-sm font-semibold transition-all duration-300 ${
                transparent
                  ? 'text-white hover:bg-white/10'
                  : 'text-cnib-navy hover:bg-slate-100'
              }`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 transition-colors ${
                  transparent ? 'ring-white/60 group-hover:ring-white' : 'ring-orange-500/60 group-hover:ring-orange-500'
                }`}
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || 'Mi perfil'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-cnib-navy/90 text-white">
                    <UserIcon className="h-4 w-4" />
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1.5">
                <LayoutDashboard className="h-4 w-4" />
                Mi panel
              </span>
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-button"
                className={`px-3 py-2 text-sm font-semibold transition-colors duration-300 hover:text-orange-500 ${
                  transparent ? 'text-white' : 'text-cnib-navy'
                }`}
              >
                Acceso socios
              </Link>
              <Link
                to="/hagase-socio"
                data-testid="nav-cta-hagase-socio-button"
                className="group flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold text-white shadow-md shadow-orange-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-600/30"
              >
                Hágase socio
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </>
          )}
        </div>

        <button
          className={`rounded-lg p-2 lg:hidden ${transparent ? 'text-white' : 'text-cnib-navy'}`}
          onClick={() => setOpen(true)}
          data-testid="nav-mobile-menu-button"
          aria-label="Abrir menú"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-cnib-navy lg:hidden"
          >
            <div className="flex h-16 items-center justify-between px-4">
              <img src={LOGO_DARK_BG} alt="CNIB" className="h-9 w-auto" />
              <button className="rounded-lg p-2 text-white" onClick={() => setOpen(false)} data-testid="nav-mobile-close-button" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-6 py-8">
              {menu.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-heading text-2xl font-bold ${isActive ? 'text-orange-500' : 'text-white'}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                {!user && (
                  <Link
                    to="/hagase-socio"
                    onClick={() => setOpen(false)}
                    className="rounded-full bg-orange-500 py-3.5 text-center font-bold text-white"
                    data-testid="nav-mobile-cta-hagase-socio"
                  >
                    Hágase socio
                  </Link>
                )}
                <Link
                  to={panelHref}
                  onClick={() => setOpen(false)}
                  className={`rounded-full py-3.5 text-center font-semibold ${
                    user ? 'bg-orange-500 text-white' : 'border border-white/25 text-white'
                  }`}
                  data-testid="nav-mobile-login-button"
                >
                  {user ? 'Mi panel' : 'Acceso socios'}
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
